import { DatabaseBackup, Download } from 'lucide-react';
import { useState } from 'react';
import {
  BACKUP_ENTITIES,
  BACKUP_ENTITY_GROUPS,
  BACKUP_ENTITY_LABELS,
  BACKUP_GROUPS,
  type BackupEntity,
  type BackupGroup,
} from '#shared/types';
import { Banner } from '#client/components/ui/Banner';
import { Button } from '#client/components/ui/Button';
import { Card } from '#client/components/ui/Card';
import { Checkbox } from '#client/components/ui/Checkbox';
import { Page } from '#client/components/ui/Page';
import { TextField } from '#client/components/ui/TextField';
import {
  type EntitySelection,
  exportUrl,
  groupSelection,
  selectedInSheetOrder,
  withEntities,
} from './backup.logic';
import { countOf } from '#client/lib/format';

/**
 * Download the lab's data as one workbook, a sheet per entity.
 *
 * Nothing here is fetched: the choices build a link, and the browser's own
 * download manager handles a file that may take a while to arrive.
 */
export function BackupPage() {
  const [selected, setSelected] = useState<EntitySelection>(() => new Set(BACKUP_ENTITIES));
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  function handleToggle(entities: readonly BackupEntity[], isChecked: boolean) {
    setSelected((current) => withEntities(current, entities, isChecked));
  }

  function handleSelectEverything() {
    setSelected(new Set(BACKUP_ENTITIES));
    setFrom('');
    setTo('');
  }

  const entities = selectedInSheetOrder(selected);

  return (
    <Page
      title="Backup & export"
      subtitle="Download the lab's data as an Excel workbook, one sheet per kind of record"
      primaryAction={
        <Button icon={<DatabaseBackup aria-hidden />} onClick={handleSelectEverything}>
          Full backup
        </Button>
      }
    >
      <div className="c-form-stack">
        <Banner tone="info">
          Master data is always exported in full. The date range narrows only the records —
          patients, reports, payments, stock and expenses. Passwords, signatures and the logo are
          never included.
        </Banner>

        {BACKUP_GROUPS.map((group) => (
          <BackupGroupCard key={group} group={group} selected={selected} onToggle={handleToggle} />
        ))}

        <DateRangeCard from={from} to={to} onFromChange={setFrom} onToChange={setTo} />

        <div>
          <DownloadButton entities={entities} from={from} to={to} />
        </div>
      </div>
    </Page>
  );
}

interface BackupGroupCardProps {
  group: BackupGroup;
  selected: EntitySelection;
  onToggle: (entities: readonly BackupEntity[], isChecked: boolean) => void;
}

function BackupGroupCard({ group, selected, onToggle }: BackupGroupCardProps) {
  const { members, chosenCount, isComplete } = groupSelection(selected, group);

  return (
    <Card
      title={BACKUP_ENTITY_GROUPS[group].label}
      subtitle={`${chosenCount} of ${members.length} selected`}
      actions={
        <Button size="sm" variant="ghost" onClick={() => onToggle(members, !isComplete)}>
          {isComplete ? 'Clear' : 'Select all'}
        </Button>
      }
    >
      <div className="c-form-grid">
        {members.map((entity) => (
          <Checkbox
            key={entity}
            label={BACKUP_ENTITY_LABELS[entity]}
            checked={selected.has(entity)}
            onChange={(event) => onToggle([entity], event.target.checked)}
          />
        ))}
      </div>
    </Card>
  );
}

interface DateRangeCardProps {
  from: string;
  to: string;
  onFromChange: (from: string) => void;
  onToChange: (to: string) => void;
}

function DateRangeCard({ from, to, onFromChange, onToChange }: DateRangeCardProps) {
  return (
    <Card title="Date range" subtitle="Leave both empty to export every record">
      <div className="c-form-grid">
        <TextField
          label="From"
          type="date"
          value={from}
          max={to || undefined}
          onChange={(event) => onFromChange(event.target.value)}
        />
        <TextField
          label="To"
          type="date"
          value={to}
          min={from || undefined}
          onChange={(event) => onToChange(event.target.value)}
        />
      </div>
    </Card>
  );
}

interface DownloadButtonProps {
  entities: readonly BackupEntity[];
  from: string;
  to: string;
}

function DownloadButton({ entities, from, to }: DownloadButtonProps) {
  if (entities.length === 0) {
    return (
      <Button variant="primary" icon={<Download aria-hidden />} disabled>
        Choose something to export
      </Button>
    );
  }

  return (
    // A real link, styled as a button: the response is a file, not JSON.
    <a
      className="c-button c-button--primary c-button--md"
      href={exportUrl(entities, from, to)}
      download
    >
      <Download aria-hidden />
      Download .xlsx ({countOf(entities.length, 'sheet')})
    </a>
  );
}
